import React, { useCallback, useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { usePageWidth } from '../responsive'
import { ParentInfoSheetProvider } from './ParentInfoSheetProvider'
import { useInfoSheetContext, sideSheetSizes } from './InfoSheetProvider'
import BaseInfoSheet, { type InfoSheetBaseProps } from './BaseInfoSheet'
import { type UseInfoSheetReturnOptions } from './useInfoSheet'

type InfoSheetProps = Omit<InfoSheetBaseProps, 'onBackClick' | 'togglePlacement'> & UseInfoSheetReturnOptions

type CleanUpRef = {
  isOpen?: () => void
  nonClosable?: () => void
}

const useDisableAutoFocus = (isOpen: boolean, disableAutoFocus: boolean): boolean => {
  const firstRenderRef = useRef(true)
  const [definitelyDisableAutoFocus, setDefinitelyDisableAutoFocus] = useState(isOpen || disableAutoFocus)
  useEffect(() => {
    if (!firstRenderRef.current) {
      setDefinitelyDisableAutoFocus(disableAutoFocus)
    } else firstRenderRef.current = false
    // initially open should only disable autofocus on first render
    // so we watch isOpen to turn off disableAutoFocus when the infosheet is closed
  }, [isOpen, disableAutoFocus])
  return definitelyDisableAutoFocus
}

/**
 * ### _Provides supplementary information to the main content. It appears on the right side or bottom of the window._
 *
 * One must use `useInfoSheet` to consume an InfoSheet. An affordance (Button, IconButton, ActionChip) should be used to show and hide the infosheet.
 */
const InfoSheet = ({
  children,
  size = 'sm',
  dockable = false,
  disableAutoFocus = false,
  nonClosable = false,

  // From useInfoSheet
  isOpen,
  open,
  close,
  transitioning,
  isBottomSheet,
  setIsBottomSheet,
  bottomSheetHeight,
  setBottomSheetHeight,

  ...props
}: InfoSheetProps): JSX.Element => {
  const cleanUpRef = useRef<CleanUpRef>({})

  const definitelyDisableAutoFocus = useDisableAutoFocus(isOpen, disableAutoFocus)

  const [panelPortalDiv, setPanelPortalDiv] = useState<HTMLDivElement | undefined>(
    document ? document.createElement('div') : undefined
  )
  const panelPortalRef = useCallback(setPanelPortalDiv, [setPanelPortalDiv])

  const { sheetPortalDiv, updateInfoSheet, closingCurrentInfoSheet, registerNonClosableSheet } = useInfoSheetContext()

  const sizeBreakpointMap = { sm: 'md', md: 'lg', lg: 'lg' }
  const isMobile = usePageWidth()({ default: true, [sizeBreakpointMap[size]]: false })
  const isDefinitelyBottomSheet = isMobile || isBottomSheet
  // The size of the sheet (width *or* height, depending on sheet placement)
  const sheetSize = !isDefinitelyBottomSheet ? sideSheetSizes[size] : bottomSheetHeight

  // Register/UnRegister nonClosable Infosheet
  useEffect(() => {
    if (nonClosable) {
      registerNonClosableSheet(open)
      // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
      cleanUpRef.current.nonClosable = () => registerNonClosableSheet(null)
    } else {
      cleanUpRef.current.nonClosable?.()
      // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
      cleanUpRef.current.nonClosable = null
    }
  }, [nonClosable, open, registerNonClosableSheet])

  // UpdateInfoSheet
  useEffect(() => {
    if (isOpen) {
      updateInfoSheet?.({ size, isBottomSheet: isDefinitelyBottomSheet, close, bottomSheetHeight })
      cleanUpRef.current.isOpen = () => closingCurrentInfoSheet(close)
    } else {
      // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
      cleanUpRef.current.isOpen = null
    }
  }, [isOpen, size, isDefinitelyBottomSheet, close, updateInfoSheet, closingCurrentInfoSheet, bottomSheetHeight])

  // Close InfoSheet
  useEffect(() => {
    if (!isOpen && !transitioning) {
      closingCurrentInfoSheet(close)
    }
  }, [isOpen, close, closingCurrentInfoSheet, transitioning])

  // Clean Up
  useEffect(
    () => () => {
      cleanUpRef.current.nonClosable?.()
      cleanUpRef.current.isOpen?.()
    },
    []
  )

  // TOGGLE PLACEMENT - Transition between bottom sheet and side sheet
  const togglePlacement = (): void => {
    setIsBottomSheet((bottom) => !bottom)
  }

  return createPortal(
    <ParentInfoSheetProvider
      bottomSheetHeight={bottomSheetHeight}
      close={close}
      isBottomSheet={isDefinitelyBottomSheet}
      isOpen={isOpen}
      nonClosable={nonClosable}
      // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
      panelPortalDiv={panelPortalDiv}
      setBottomSheetHeight={setBottomSheetHeight}
      sheetSize={sheetSize}
      transitioning={transitioning}
    >
      <BaseInfoSheet
        disableAutoFocus={definitelyDisableAutoFocus}
        dockable={dockable && !isMobile}
        isBottomSheet={isDefinitelyBottomSheet}
        isOpen={isOpen}
        nonClosable={nonClosable}
        // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
        panelPortalRef={panelPortalRef}
        sheetSize={sheetSize}
        togglePlacement={togglePlacement}
        transitioning={transitioning}
        {...props}
      >
        {children}
      </BaseInfoSheet>
    </ParentInfoSheetProvider>,
    sheetPortalDiv
  )
}

export default InfoSheet
